import { ExpandMore, HistoryOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  List,
  Skeleton,
  styled,
  Typography,
} from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { getConversations } from '../../redux/actions/conversationActions';
import { selectAppStatus, setRecentChatsOpen } from '../../redux/reducers/appStatusReducer';
import { selectConversations } from '../../redux/reducers/conversationReducer';
import { selectNavDrawer } from '../../redux/reducers/navDrawerReducer';
import { AppDispatch } from '../../redux/store';
import { Conversation } from '../../Types/conversation';
import { CardSize } from '../../Types/enums';
import ChatCard from '../Chat/ChatCard';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';

const FilterControlsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

const StyledList = styled(List)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  '& > *:not(:last-child)': {
    marginBottom: '10px',
  },
  '& > *:nth-last-of-type(2)': {
    marginBottom: '0px',
  },
  padding: '10px 0px',
});

const LoadMoreButton = styled(Button)({
  margin: '4px 0px 8px 0px',
  color: 'black',
  fontWeight: 'bold',
});

interface ChatListProps {
  onConversationUpdated: (
    conversationId?: string,
    projectId?: string,
    shouldFetchConversations?: boolean
  ) => void;
  chatListHeight: string;
}

const ChatList = ({ onConversationUpdated, chatListHeight }: ChatListProps) => {
  const analytics = useAnalytics();
  const navigate = useClassicNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { conversations, totalCount, isLoadingConversations } = useSelector(selectConversations);

  const { recentChatsOpen, isSwitchingApps } = useSelector(selectAppStatus);
  const { searchQuery, conversationsOnly } = useSelector(selectNavDrawer);

  const shouldShowLoadMoreButton = conversations.length > 0 && conversations.length < totalCount;

  const loadMoreConversations = () => {
    analytics.event(AnalyticsEvent.WM_NIGEL_LOAD_MORE_CONVERSATIONS, undefined, {
      numConversationsOld: conversations.length,
    });

    dispatch(getConversations({ pageAction: 'increment', searchQuery }));
  };

  const hasConversations = conversations.length > 0;

  const ChatCardSkeleton = () => (
    <div style={{ width: '100%', padding: '6px 0px' }}>
      <Skeleton variant="rounded" height={80} width="100%" />
    </div>
  );

  return (
    <Accordion
      expanded={recentChatsOpen}
      onChange={() => dispatch(setRecentChatsOpen(!recentChatsOpen))}
      sx={{
        '.MuiAccordionSummary-root.Mui-expanded': { height: '48px', minHeight: '48px' },
        '.MuiAccordionSummary-content': { margin: '0' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FilterControlsContainer>
          <div style={{ display: 'flex', gap: '5px' }}>
            <HistoryOutlined sx={{ height: '20px', width: '20px' }} />
            <Typography variant="subtitle2" sx={{ fontSize: '15px' }}>
              Recent Chats
            </Typography>
          </div>
          <Typography
            variant="caption2"
            sx={{ '&:hover': { cursor: 'pointer' }, fontWeight: '600' }}
            onClick={(e) => {
              e.stopPropagation();
              navigate('/search/chats');
            }}
          >
            {'View All >'}
          </Typography>
        </FilterControlsContainer>
      </AccordionSummary>

      <AccordionDetails
        style={{
          height: chatListHeight,
          padding: '0px',
          overflow: 'auto',
          maxHeight: 'fit-content',
        }}
      >
        {isLoadingConversations || isSwitchingApps ? (
          <div style={{ padding: '10px 0px' }}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} style={{ padding: '0 10px' }}>
                <ChatCardSkeleton />
              </div>
            ))}
          </div>
        ) : !hasConversations ? (
          <Typography variant="body2" sx={{ color: '#070154', padding: '16px' }}>
            No conversations found
          </Typography>
        ) : (
          <>
            <StyledList>
              {conversations.map((conversation: Conversation) => (
                <ChatCard
                  key={conversation.id}
                  conversation={conversation}
                  onConversationCardClick={() =>
                    onConversationUpdated(conversation.id, conversation.folderId, false)
                  }
                  searchQuery={
                    conversationsOnly === true || conversationsOnly === undefined ? searchQuery : ''
                  }
                  IsSearchFocused={false}
                  size={CardSize.SMALL}
                />
              ))}
              <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                {isLoadingConversations ? (
                  <div style={{ padding: '0 10px 10px 10px', width: '100%' }}>
                    <Skeleton variant="text" width={120} />
                  </div>
                ) : (
                  shouldShowLoadMoreButton && (
                    <LoadMoreButton onClick={loadMoreConversations}>
                      <Typography variant="caption2">+ Load More</Typography>
                    </LoadMoreButton>
                  )
                )}
              </div>
            </StyledList>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ChatList;
